/** What lies on bare ground: pebbles on stone, lilies on water, shards in ore, rubble where rock broke. */
import { KIND, type Kind } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { hash2 } from '@core/math';
import { TILE_TOP } from '@core/palette';

import type { Instances } from './instances';

/** Chunks of rubble on a broken tile. */
const RUBBLE = 7;
const _rock = new THREE.Color();

/**
 * Rubble on tile `i` at direction `c`: rock of `kind` a charge broke, lying there to be gathered.
 * Chunks in the rock's colour, each a little lighter or darker, heaped round the middle; broken
 * ore shows its crystals.
 */
export function placeRubble(ins: Instances, i: number, c: THREE.Vector3, kind: Kind): void {
  for (let k = 0; k < RUBBLE; k++) {
    const a = hash2(i, 80 + k) * 6.283;
    const r = k === 0 ? 0.03 : 0.12 + hash2(i, 90 + k) * 0.26;
    _rock.setHex(TILE_TOP[kind]).multiplyScalar(0.72 + 0.4 * hash2(i, 100 + k));
    const size = (k === 0 ? 1.5 : 0.7) + hash2(i, 110 + k) * 0.7;
    ins.put('rubble', c, Math.cos(a) * r, Math.sin(a) * r, a * 3, size, _rock);
  }
  if (kind === KIND.ORE)
    for (let k = 0; k < 2; k++) {
      const a = hash2(i, 120 + k) * 6.283;
      ins.put('shard', c, Math.cos(a) * 0.2, Math.sin(a) * 0.2, a, 0.5 + hash2(i, 130 + k) * 0.4);
    }
}

/**
 * The ground props of tile `i` at direction `c`; `lit`: an extractor draws from it, and its crystals
 * glow brighter, pulsing. Returns false for other kinds.
 */
export function placeGround(
  ins: Instances,
  i: number,
  c: THREE.Vector3,
  kind: Kind,
  lit = false,
): boolean {
  const r1 = hash2(i, 1);
  const r2 = hash2(i, 2);
  const r3 = hash2(i, 3);
  const yaw = r1 * 6.283;
  switch (kind) {
    case KIND.STONE: {
      const stone = lit ? 'stone.lit' : 'stone';
      ins.put(stone, c, (r3 - 0.5) * 0.4, (r1 - 0.5) * 0.4, yaw, 0.8 + r2 * 0.5);
      if (r2 < 0.5)
        ins.put(stone, c, (r1 - 0.5) * 0.44, (r2 - 0.5) * 0.44, yaw + 2, 0.55 + r3 * 0.3);
      return true;
    }
    case KIND.WATER:
      if (r2 < 0.55)
        ins.put(
          'lily',
          c,
          (r3 - 0.5) * 0.36,
          (r1 - 0.5) * 0.36,
          yaw,
          0.8 + r2 * 0.5,
          undefined,
          0.004,
        );
      return true;
    case KIND.ORE:
      for (let k = 0; k < 3; k++) {
        const a = yaw + k * 2.09;
        const r = 0.16 + hash2(i, 60 + k) * 0.14;
        ins.put(
          lit ? 'shard.lit' : 'shard',
          c,
          Math.cos(a) * r,
          Math.sin(a) * r,
          a,
          0.6 + hash2(i, 70 + k) * 0.7,
        );
      }
      return true;
    default:
      return false;
  }
}
